// libs
import * as React from 'react'
import showdown from 'showdown'
import { Root, createRoot } from 'react-dom/client'

// interfaces / constants
import {
  DisplaySettings,
  mapDisplayModeToConst,
  POST_DISPLAY_MODE_DETAIL,
  PostDisplayMode,
  Purpose
} from 'src/interfaces/posts'

// constants
import { CLASS_PREFIX } from 'src/constants/'
import { dropUnsafeTargets, escapeForMarkup } from 'src/utils/safe_url/safe_url'

// classes
import BlurredContent, { IBlurred } from 'src/components/blurred_content/blurred_content'
import PreviewWithConsent from 'src/components/gdpr/preview_with_consent/preview_with_consent'

// styles
import { ImageShortcode, Shortcode, ShortcodeType, YoutubeShortcode } from '@lopo-tech/lopo-api'
import NavigationItem from 'src/components/navigation_item/navigation_item'
import './body_markdown.scss'

export const _classPrefix = `${CLASS_PREFIX}body-markdown__`

const showdownOptions: showdown.ConverterOptions = {
  noHeaderId: true,
  openLinksInNewWindow: true,
  simplifiedAutoLink: true,
  tables: true
}

const showdownConverter = new showdown.Converter(showdownOptions)

type BodyMarkdownSettings = { show: boolean }

const _settings: DisplaySettings<BodyMarkdownSettings> = {
  admin: { basic_ingredient: { show: true } },
  banner: { basic_ingredient: { show: false } },
  compact: { basic_ingredient: { show: false } },
  detail: { basic_ingredient: { show: true } },
  preview: { basic_ingredient: { show: true } }
}

export type IProps = {
  blurred?: IBlurred;
  displayMode: PostDisplayMode;
  postDetailsURL?: string;
  purpose: Purpose;
  shortcodes: Shortcode[];
  teaserContent?: string | null;
  text?: string | null;
};

export default class BodyMarkdown extends React.PureComponent<IProps> {
  private bodyMarkdown: React.RefObject<HTMLDivElement> = React.createRef()

  private cls = ''
  private clsShortcodePlaceholder = ''
  private shortcodeRoots: Map<HTMLElement, Root> = new Map()

  constructor (props: IProps) {
    super(props)
    this.cls = `${_classPrefix}${mapDisplayModeToConst[props.displayMode]}`
    this.clsShortcodePlaceholder = `${this.cls}__shortcode-placeholder`
  }

  public render () {
    const {
      blurred,
      displayMode,
      postDetailsURL,
      purpose,
      shortcodes,
      teaserContent,
      text
    } = this.props

    const htmlContent: string =
      displayMode === POST_DISPLAY_MODE_DETAIL && text
        ? this.parseContent(text, shortcodes)
        // Only this branch: the detail branch renders converted markdown and
        // escaping it would print the markup instead of applying it.
        : escapeForMarkup(teaserContent ?? '')
    const bodyElement = (
      <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
    )

    const settings = _settings[displayMode][purpose]

    return settings?.show
      ? (
        <NavigationItem target="_self" url={postDetailsURL}>
          <div className={this.cls} ref={this.bodyMarkdown}>
            {blurred
              ? (
                <BlurredContent blurred={blurred}>{bodyElement}</BlurredContent>
              )
              : (
                bodyElement
              )}
          </div>
        </NavigationItem>
      )
      : null
  }

  public componentDidMount () {
    this.renderShortcodeComponents()
  }

  public getSnapshotBeforeUpdate () {
    this.cleanupShortcodeRoots()
    return null
  }

  public componentDidUpdate () {
    this.renderShortcodeComponents()
  }

  public componentWillUnmount () {
    this.cleanupShortcodeRoots()
  }

  private stopNavigation = (event: React.MouseEvent<HTMLElement>): void => {
    event.stopPropagation()
  }

  private parseContent (content: string, shortcodes: Shortcode[]): string {
    return this.parseMarkdownWithShortCodes(content, shortcodes)
  }

  private parseMarkdownWithShortCodes (content: string, shortcodes: Shortcode[]): string {
    // Checked before the shortcode placeholders go in. That ordering is not what
    // protects them today: dropUnsafeTargets strips no tags of its own, so they
    // come through the parser unchanged either way, measured.
    //
    // It does lose certain elements sitting at the very start of the fragment - a
    // documented property of parsing one as a document, enumerated where the
    // function lives rather than repeated here, because one of them behaves
    // differently in the two parsers this code meets. That case is reachable from
    // here: measured, a body opening with such a block is emitted by the converter
    // with the block still first. The same element further in survives.
    //
    // The ordering becomes load-bearing the moment anyone filters tags or
    // attributes here - and at that point nothing would fail. The placeholders
    // would simply stop being found, and every image and video in every markdown
    // post would go missing in silence. Keep the checking in front of the
    // inserting.
    const converted: string = dropUnsafeTargets(showdownConverter.makeHtml(content))
    if (!shortcodes.length) {
      return converted
    }

    return converted.replace(/{{[\w\d_-]+}}/g, (match: string) =>
      this.replaceWithShortCodes(match, shortcodes)
    )
  }

  private replaceWithShortCodes (match: string, shortcodes: Shortcode[]): string {
    const shortcode = shortcodes.find((sc) => sc.placeholder === match)
    if (!shortcode) {
      return match
    }

    return `<span class='${this.clsShortcodePlaceholder}' data-shortcode-id='${shortcode.id}'></span>`
  }

  private renderShortcodeComponents (): void {
    const container = this.bodyMarkdown.current
    if (!container) {
      return
    }

    const placeholders = container.querySelectorAll<HTMLElement>(`.${this.clsShortcodePlaceholder}`)
    placeholders.forEach((placeholder) => {
      const shortcodeId = placeholder.dataset.shortcodeId
      if (!shortcodeId) {
        return
      }
      const shortcode = this.props.shortcodes.find((sc) => sc.id === shortcodeId)
      if (!shortcode) {
        return
      }

      const content = this.renderShortcode(shortcode)
      if (!content) {
        return
      }

      let root = this.shortcodeRoots.get(placeholder)
      if (!root) {
        root = createRoot(placeholder)
        this.shortcodeRoots.set(placeholder, root)
      }

      root.render(content)
    })
  }

  private cleanupShortcodeRoots (): void {
    this.shortcodeRoots.forEach((root) => {
      root.unmount()
    })
    this.shortcodeRoots.clear()
  }

  private renderShortcode (shortcode: Shortcode): React.ReactNode {
    switch (shortcode.type) {
      case ShortcodeType.Image: {
        return this.renderImageShortcode(shortcode as ImageShortcode)
      }
      case ShortcodeType.Youtube: {
        return this.renderYoutubeShortcode(shortcode as YoutubeShortcode)
      }
      default:
        return null
    }
  }

  private renderImageShortcode (shortcode: ImageShortcode): React.ReactNode {
    const { asset, id } = shortcode
    if (!asset) {
      return null
    }

    return (
      <figure
        className={`${this.cls}__image`}
        data-shortcode-id={id}
        onClick={this.stopNavigation}
      >
        <img
          src={asset.file.previewUrl}
          alt={asset.altText || ''}
        />
        {asset.name && (
          <figcaption className={`${this.cls}__image-caption`}>
            {asset.name}
          </figcaption>
        )}
        {asset.source && (
          <p className={`${this.cls}__image-source`}>
            {asset.source}
          </p>
        )}
      </figure>
    )
  }

  private renderYoutubeShortcode (shortcode: YoutubeShortcode): React.ReactNode {
    const { id, videoId } = shortcode

    if (!videoId) {
      return null
    }

    return (
      <div
        className={`${this.cls}__video`}
        data-shortcode-id={id}
        onClick={this.stopNavigation}
      >
        <PreviewWithConsent type="video" source="youtube">
          <div className={`${this.cls}__video-container`}>
            <iframe
              src={`https://www.youtube-nocookie.com/embed/${videoId}?rel=0`}
              allowFullScreen
              title="YouTube video player"
              className={`${this.cls}__video-frame`}
            />
          </div>
        </PreviewWithConsent>
      </div>
    )
  }
}
